import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Animated, Easing, Platform } from 'react-native';
import {
  Box, HStack, VStack, Text, Button, ButtonText, ButtonIcon, Icon, Pressable,
  Tooltip, TooltipContent, TooltipText,
} from '@gluestack-ui/themed';
import { AlertCircle, CircleCheckBig } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useOrbReducedMotion } from '@/common/components/presence/useOrbReducedMotion';
import { useEvaluationGradingConfig } from '../../../../lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';
import {
  convertEvaluationsToPerformances,
  calculateTaskPerformance as calcTaskPerf,
  formatPerformanceLevelDisplay,
} from '../../../../lecturer/EvaluationDesk/utils/gradingConversions';
import { isEditedSinceEvaluation, useTaskSubmissionState } from '../../stores/TaskSubmissionStore';
import { useDialog } from '../../../../common/context/DialogContext';
import { isAtLeastLevel } from './evaluation/levels';

interface EvaluationCardProps {
  onSubmit?: () => void;
  /** Wired in S3 extended-wait cancel — preserved for parent compatibility. */
  // eslint-disable-next-line react/no-unused-prop-types
  onCancelEvaluation?: () => void;
  isMarkedAsDone: boolean;
  gated?: boolean;
  readonly?: boolean;
}

const useNative = Platform.OS !== 'web';

const LEVEL_FILLED: Record<string, number> = {
  very_good: 4,
  good: 3,
  satisfactory: 2,
  needs_improvement: 1,
};

const BAR_INDEXES = [0, 1, 2, 3] as const;
const DIMENSION_REVEAL_MS = 200;
const RESULT_REVEAL_MS = 220;
const PENDING_SWEEP_MS = 1400;
const PENDING_SWEEP_PAUSE_MS = 300;

/** Stable minimum footprint while concurrent dimensions are still arriving. */
const QUIET_DIMS_MIN_HEIGHT = 44;
const DIMENSION_SLOT_MIN_WIDTH = 80;

/** Dimension key as a sentence word: `formal` → `Formal`, `critical_thinking` → `Critical thinking`. */
const partName = (dim: string) => {
  const words = dim.replace(/_/g, ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

const TICK_WIDTH = 12;
const TICK_HEIGHT = 6;

/**
 * Solid ticks are the level reached. On a gated draft, outlined ticks are the ones
 * the part still needs for its required level; grey ticks are beyond it.
 */
const LevelBars: React.FC<{
  level: string;
  color: string;
  requiredFilled?: number;
  testID?: string;
}> = ({
  level, color, requiredFilled = 0, testID,
}) => {
  const filled = LEVEL_FILLED[level] ?? 0;
  return (
    <HStack space="xs" alignItems="center" testID={testID}>
      {BAR_INDEXES.map((i) => {
        const isFilled = i < filled;
        const isStillRequired = !isFilled && i < requiredFilled;
        let bg = '$borderLight';
        if (isFilled) bg = color;
        else if (isStillRequired) bg = 'transparent';
        return (
          <Box
            key={i}
            w={TICK_WIDTH}
            h={TICK_HEIGHT}
            borderRadius="$xs"
            bg={bg}
            borderWidth={isStillRequired ? 1.5 : 0}
            borderColor={color}
            testID={testID && isStillRequired ? `${testID}-target-${i}` : undefined}
          />
        );
      })}
    </HStack>
  );
};

const PendingLevelBars: React.FC<{ testID: string }> = ({ testID }) => {
  const reducedMotion = useOrbReducedMotion();
  const phase = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (reducedMotion) {
      phase.setValue(0);
      return undefined;
    }

    phase.setValue(0);
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(phase, {
          toValue: 1,
          duration: PENDING_SWEEP_MS,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: useNative,
        }),
        Animated.delay(PENDING_SWEEP_PAUSE_MS),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [phase, reducedMotion]);

  return (
    <HStack space="xs" alignItems="center" testID={testID}>
      {BAR_INDEXES.map((index) => {
        const center = 0.2 + index * 0.2;
        const opacity = reducedMotion ? 0.28 : phase.interpolate({
          inputRange: [0, center - 0.15, center, center + 0.15, 1],
          outputRange: [0.18, 0.18, 0.62, 0.18, 0.18],
        });
        return (
          <Animated.View key={index} style={{ opacity }}>
            <Box w={TICK_WIDTH} h={TICK_HEIGHT} bg="$borderMedium" borderRadius="$xs" />
          </Animated.View>
        );
      })}
    </HStack>
  );
};

const DimensionSlotReveal: React.FC<{
  dim: string;
  level?: string;
  color: string;
  revealed: boolean;
  animate: boolean;
}> = ({
  dim, level, color, revealed, animate,
}) => {
  const reducedMotion = useOrbReducedMotion();
  const opacity = useRef(new Animated.Value(revealed && animate && !reducedMotion ? 0 : revealed ? 1 : 0)).current;
  const translateY = useRef(new Animated.Value(revealed && animate && !reducedMotion ? 4 : 0)).current;

  useEffect(() => {
    if (!revealed) {
      opacity.setValue(0);
      translateY.setValue(4);
      return;
    }
    if (!animate || reducedMotion) {
      opacity.setValue(1);
      translateY.setValue(0);
      return;
    }
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: DIMENSION_REVEAL_MS,
        easing: Easing.out(Easing.ease),
        useNativeDriver: useNative,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: DIMENSION_REVEAL_MS,
        easing: Easing.out(Easing.ease),
        useNativeDriver: useNative,
      }),
    ]).start();
  }, [animate, opacity, reducedMotion, revealed, translateY]);

  return (
    <VStack
      space="xs"
      alignItems="flex-start"
      minWidth={DIMENSION_SLOT_MIN_WIDTH}
      mr="$2"
      mb="$1"
      testID={`evaluation-dimension-slot-${dim}`}
      data-arrived={revealed}
      data-dimension={dim}
    >
      {Platform.OS === 'web' ? React.createElement('span', {
        'data-dimension-slot-state': 'true',
        'data-dimension': dim,
        'data-arrived': revealed ? 'true' : 'false',
        'aria-hidden': true,
        style: { display: 'none' },
      }) : null}
      <Box minHeight={12} minWidth={40} justifyContent="center">
        {revealed && level ? (
          <Animated.View style={{ opacity, transform: [{ translateY }] }}>
            <LevelBars level={level} color={color} testID={`level-bars-${dim}`} />
          </Animated.View>
        ) : (
          <PendingLevelBars testID={`pending-bars-${dim}`} />
        )}
        <Text
          size="xs"
          color={revealed ? '$textSubtle' : '$textHint'}
          fontWeight="medium"
          textTransform="uppercase"
          letterSpacing="$sm"
          numberOfLines={1}
        >
          {dim}
        </Text>
      </Box>
    </VStack>
  );
};

const EvalResultsReveal: React.FC<{ animate: boolean; children: React.ReactNode }> = ({ animate, children }) => {
  const reducedMotion = useOrbReducedMotion();
  const opacity = useRef(new Animated.Value(animate && !reducedMotion ? 0 : 1)).current;
  const scale = useRef(new Animated.Value(animate && !reducedMotion ? 0.92 : 1)).current;

  useEffect(() => {
    if (!animate || reducedMotion) return;
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1, duration: RESULT_REVEAL_MS, easing: Easing.out(Easing.ease), useNativeDriver: useNative,
      }),
      Animated.timing(scale, {
        toValue: 1, duration: RESULT_REVEAL_MS, easing: Easing.out(Easing.ease), useNativeDriver: useNative,
      }),
    ]).start();
  }, [animate, reducedMotion, opacity, scale]);

  if (!animate || reducedMotion) {
    return <Box testID="evaluation-result">{children}</Box>;
  }

  return (
    <Animated.View
      style={{ opacity, transform: [{ scale }] }}
      testID="evaluation-result"
    >
      {children}
    </Animated.View>
  );
};

/**
 * Compact horizontal evaluation card showing score and dimension levels.
 */
const EvaluationCard: React.FC<EvaluationCardProps> = ({
  onSubmit,
  isMarkedAsDone,
  gated = false,
  readonly = false,
}) => {
  const {
    evalDimensionKeys, dimensionColors, levelWeights, dimensionMaxWeights, performanceLevels,
  } = useEvaluationGradingConfig();
  const { showDialog } = useDialog();
  const [openRequirementDim, setOpenRequirementDim] = useState<string | null>(null);
  const {
    canSubmit, isEvaluating, lastSavedAt, evaluation, evaluatedAt, expectedDims, minLevelsEffective,
  } = useTaskSubmissionState();
  const isOutdated = isEditedSinceEvaluation(lastSavedAt, evaluatedAt);
  const performanceLevelDisplay = useMemo(() => {
    const evaluationData = evaluation as Record<string, { level?: string } | undefined>;
    const present = evalDimensionKeys.filter(
      (dim) => evaluation && evaluationData[dim] && typeof evaluationData[dim]?.level === 'string' && evaluationData[dim]?.level !== 'n/a',
    );
    const filteredEval = present.reduce((acc: Record<string, unknown>, dim) => {
      acc[dim] = evaluationData[dim];
      return acc;
    }, {});
    const performances = convertEvaluationsToPerformances(filteredEval as Record<string, { level: string }>, levelWeights);
    const attainment = calcTaskPerf(performances, dimensionMaxWeights);
    return formatPerformanceLevelDisplay(attainment, performanceLevels);
  }, [evaluation, evalDimensionKeys, levelWeights, dimensionMaxWeights, performanceLevels]);
  const { t } = useTranslation();

  const evaluationData = (evaluation || {}) as Record<string, { level?: string } | undefined>;
  const hasEvaluatedDim = Object.keys(evaluationData).some(
    (dim) => typeof evaluationData[dim]?.level === 'string' && evaluationData[dim]?.level !== 'n/a',
  );
  const slotDimensions = (expectedDims.length > 0 ? expectedDims : evalDimensionKeys);

  if (!isEvaluating && (!evaluation || Object.keys(evaluation || {}).length === 0)) {
    return (
      <Box bg="$bgSubtle" px="$4" py="$3" borderRadius="$lg">
        <Text size="xs" color="$textHint">
          {t('student.task.aiTutor.evaluationCard.noEvaluation')}
        </Text>
      </Box>
    );
  }

  const handleSubmit = () => {
    if (!onSubmit) return;
    showDialog({
      title: t('student.task.aiTutor.evaluationCard.submitTask.title'),
      message: t('student.task.aiTutor.evaluationCard.submitTask.message'),
      buttons: [
        { text: t('student.task.aiTutor.evaluationCard.submitTask.cancel'), action: 'secondary' },
        {
          text: t('student.task.aiTutor.evaluationCard.submitTask.submit'),
          action: 'positive',
          onPress: () => onSubmit(),
        },
      ],
    });
  };

  const isDraft = !isMarkedAsDone && !readonly;
  const arrivedDimCount = slotDimensions.filter((dim) => {
    const v = evaluationData[dim];
    return typeof v?.level === 'string' && v.level !== 'n/a';
  }).length;
  const allDimsArrived = arrivedDimCount >= slotDimensions.length && slotDimensions.length > 0;
  const showProgressiveEvaluating = isEvaluating && slotDimensions.length > 0 && !allDimsArrived;
  const dimsForDisplay = showProgressiveEvaluating
    ? slotDimensions
    : slotDimensions.filter((dim) => {
      const v = evaluationData[dim];
      return typeof v?.level === 'string' && v.level !== 'n/a';
    });
  const showFinalScore = !isEvaluating || allDimsArrived;
  const shouldAnimateReveal = isEvaluating && allDimsArrived;
  const showGate = gated && isDraft;
  let requiredLevels: Record<string, string> = {};
  if (showGate && minLevelsEffective) {
    requiredLevels = typeof minLevelsEffective === 'string'
      ? Object.fromEntries(evalDimensionKeys.map((dim) => [dim, minLevelsEffective]))
      : minLevelsEffective;
  }
  const bandHeadline = showFinalScore && hasEvaluatedDim && performanceLevelDisplay ? performanceLevelDisplay : '—';
  let gateHeadline = t('student.task.aiTutor.evaluationCard.notReadyToSubmit');
  let isNotReadyYet = false;
  if (isEvaluating) {
    gateHeadline = '—';
  } else if (isOutdated) {
    gateHeadline = t('student.task.aiTutor.evaluationCard.evaluateAgain');
  } else if (canSubmit) {
    gateHeadline = t('student.task.aiTutor.evaluationCard.readyToSubmit');
  } else {
    isNotReadyYet = true;
  }

  return (
    <VStack
      space="md"
      bg="$bgSubtle"
      px="$4"
      py="$3"
      borderRadius="$lg"
      borderWidth={1}
      borderColor="$borderLight"
      testID="evaluation-card"
    >
      <Text
        size="xs"
        color="$textSubtle"
        fontWeight="semibold"
        textTransform="uppercase"
        letterSpacing="$sm"
        testID="evaluation-card-title"
      >
        {t('student.task.aiTutor.evaluationCard.title')}
      </Text>
      {showProgressiveEvaluating ? (
        <Box
          testID="evaluation-card-reserved-footprint"
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          aria-hidden
        >
          <HStack space="md" flexWrap="wrap" minHeight={QUIET_DIMS_MIN_HEIGHT}>
            {slotDimensions.map((dim) => {
              const evalObj = evaluationData[dim];
              const level = typeof evalObj?.level === 'string' && evalObj.level !== 'n/a'
                ? evalObj.level
                : undefined;
              const color = dimensionColors[dim] || '#666666';
              return (
                <DimensionSlotReveal
                  key={dim}
                  dim={dim}
                  level={level}
                  color={color}
                  revealed={Boolean(level)}
                  animate={Boolean(level)}
                />
              );
            })}
          </HStack>
        </Box>
      ) : (
        <EvalResultsReveal animate={shouldAnimateReveal}>
          <VStack space="md">
            {showGate ? (
              <VStack space="xs">
                <Text
                  size="md"
                  color={isEvaluating ? '$textHint' : '$textHeading'}
                  fontWeight="semibold"
                  testID="evaluation-gate-headline"
                >
                  {gateHeadline}
                </Text>
                {isNotReadyYet ? (
                  <Text size="sm" color="$textSubtle" testID="evaluation-gate-next-step">
                    {t('student.task.aiTutor.evaluationCard.notReadyNextStep')}
                  </Text>
                ) : null}
              </VStack>
            ) : (
              <HStack alignItems="baseline" space="xs">
                <Text
                  size="xl"
                  color={showFinalScore && hasEvaluatedDim ? '$textSubtle' : '$textHint'}
                  fontWeight="bold"
                  testID="evaluation-score"
                >
                  {bandHeadline}
                </Text>
                {isOutdated && (
                  <Text size="xs" color="$amber600" ml="$2">
                    (edited)
                  </Text>
                )}
              </HStack>
            )}

            <HStack space="md" flexWrap="wrap">
              {dimsForDisplay.map((dim) => {
                const evalObj = evaluationData[dim];
                const level = evalObj?.level as string;
                const color = dimensionColors[dim] || '#666666';
                const requiredLevel = requiredLevels[dim];
                const isShort = Boolean(requiredLevel) && !isAtLeastLevel(level, requiredLevel);
                const requirement = isShort
                  ? t('student.task.aiTutor.evaluationCard.requiredLevelTooltip', {
                    part: partName(dim),
                    level: performanceLevels.find((item) => item.id === requiredLevel)?.label ?? requiredLevel,
                    defaultValue: 'This task requires {{part}} to be at least level “{{level}}”.',
                  })
                  : '';
                return (
                  <VStack key={dim} space="xs" alignItems="flex-start" minWidth={DIMENSION_SLOT_MIN_WIDTH} mr="$2" mb="$1">
                    <HStack space="xs" alignItems="center" minHeight={16}>
                      <LevelBars
                        level={level}
                        color={color}
                        requiredFilled={isShort ? LEVEL_FILLED[requiredLevel] ?? 0 : 0}
                        testID={`level-bars-${dim}`}
                      />
                      {isShort ? (
                        <Tooltip
                          placement="top"
                          closeOnClick={false}
                          testID={`dimension-gate-tooltip-${dim}`}
                          isOpen={openRequirementDim === dim}
                          onOpen={() => setOpenRequirementDim(dim)}
                          onClose={() => setOpenRequirementDim((current) => (current === dim ? null : current))}
                          trigger={(triggerProps) => (
                            <Pressable
                              {...triggerProps}
                              onPress={() => setOpenRequirementDim((current) => (current === dim ? null : dim))}
                              ml="$1"
                              hitSlop={8}
                              accessibilityRole="button"
                              accessibilityLabel={requirement}
                              testID={`dimension-gate-mark-${dim}-alert`}
                            >
                              <Icon as={AlertCircle} size="xs" color="$textBody" />
                            </Pressable>
                          )}
                        >
                          <TooltipContent maxWidth={240} bg="$backgroundDark800">
                            <TooltipText size="xs">{requirement}</TooltipText>
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </HStack>
                    <Text
                      size="xs"
                      color="$textSubtle"
                      fontWeight="medium"
                      textTransform="uppercase"
                      letterSpacing="$sm"
                      numberOfLines={1}
                    >
                      {dim}
                    </Text>
                  </VStack>
                );
              })}
            </HStack>
          </VStack>
        </EvalResultsReveal>
      )}

      {showGate && onSubmit && !isEvaluating && (
        <HStack justifyContent="flex-start" mt="$1">
          <Button
            size="xs"
            action="primary"
            h={28}
            onPress={canSubmit ? handleSubmit : undefined}
            isDisabled={!canSubmit}
            accessibilityState={{ disabled: !canSubmit }}
            testID="submit-button"
          >
            <ButtonIcon as={CircleCheckBig} size="xs" mr="$1" />
            <ButtonText size="xs">{t('common.buttons.submit', 'Submit')}</ButtonText>
          </Button>
        </HStack>
      )}
      {isMarkedAsDone && (
        <HStack alignItems="center" space="xs" mt="$1">
          <Icon as={CircleCheckBig} size="xs" color="$green600" />
          <Text size="xs" color="$textHint">
            {t('common.labels.done', 'Done')}
          </Text>
        </HStack>
      )}
    </VStack>
  );
};

export default EvaluationCard;
